import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Mic,
    Square,
    Send,
    ShieldCheck,
    X,
    RefreshCcw,
    CheckCircle2,
    Pencil,
} from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import useSpeechRecognition from "../hooks/useSpeechRecognition";

const STAGES = {
    RECORDING: "recording",
    DRAFT: "draft",
    CONFIRMING: "confirming",
    SENT: "sent",
};

const SEND_PATTERN = /\b(send( it)?|yes|confirm|go|ok(ay)?|deliver)\b/i;
const CANCEL_PATTERN = /\b(cancel|no|stop|wait|abort)\b/i;

const BAR_DELAYS = [0, 120, 240, 360, 480, 360, 240, 120, 0];

/**
 * VoiceReply — reusable voice reply / dictation flow.
 *   props: recipient (string, required), threadId (string), onSent({text, ts}), onClose()
 *   Stages: RECORDING → DRAFT → CONFIRMING → SENT
 */
export const VoiceReply = ({ recipient, threadId, onSent, onClose }) => {
    const { speak } = useClara();
    const sr = useSpeechRecognition();
    const [stage, setStage] = useState(STAGES.RECORDING);
    const [draft, setDraft] = useState("");
    const startedRef = useRef(false);
    const draftFocusedRef = useRef(false);

    // ---- handlers --------------------------------------------------------
    const handleRecordingResult = useCallback(
        (text, err) => {
            if (err === "unsupported") {
                // No real STT — stay in DRAFT so user can type
                setStage(STAGES.DRAFT);
                return;
            }
            if (err) {
                speak(
                    "I couldn't hear you. Tap Record again, or type your reply below.",
                );
                setStage(STAGES.DRAFT);
                return;
            }
            setDraft(text || "");
            setStage(STAGES.DRAFT);
            if (text) speak(`I heard: ${text}. Review your reply, then tap Send.`);
        },
        [speak],
    );

    const handleConfirmResult = useCallback(
        (text, err) => {
            if (err) return; // ignore — user can still tap the button
            if (SEND_PATTERN.test(text)) {
                doSend();
            } else if (CANCEL_PATTERN.test(text)) {
                doCancelConfirm();
            } else {
                speak(`I heard "${text}". Say "send it" to confirm, or "cancel".`);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const startRecording = useCallback(() => {
        setStage(STAGES.RECORDING);
        setDraft("");
        sr.stop();
        speak(`Ready to reply to ${recipient}. Speak your message now.`, {
            onEnd: () => {
                if (sr.supported) sr.start(handleRecordingResult);
            },
        });
    }, [sr, speak, recipient, handleRecordingResult]);

    const stopRecording = () => {
        const captured = sr.transcript || "";
        sr.stop();
        if (captured.trim()) {
            setDraft(captured.trim());
            setStage(STAGES.DRAFT);
            speak(`I heard: ${captured.trim()}. Review your reply, then tap Send.`);
        } else {
            setStage(STAGES.DRAFT);
            speak("Recording stopped. Type your reply or tap Record again.");
        }
    };

    const requestSend = () => {
        if (!draft.trim()) {
            speak("Your reply is empty. Record or type a message first.");
            return;
        }
        sr.stop();
        setStage(STAGES.CONFIRMING);
        speak(
            `Ready to send to ${recipient}? Say "send it" to confirm, or tap Confirm Send. Say "cancel" to stop.`,
            {
                onEnd: () => {
                    if (sr.supported) sr.start(handleConfirmResult);
                },
            },
        );
    };

    const doSend = () => {
        sr.stop();
        setStage(STAGES.SENT);
        speak(`Sent to ${recipient}.`);
        onSent?.({ text: draft.trim(), ts: Date.now(), threadId });
    };

    const doCancelConfirm = () => {
        sr.stop();
        setStage(STAGES.DRAFT);
        speak("Send cancelled. Tap Send when you're ready.");
    };

    const doCancelAll = () => {
        sr.stop();
        speak("Reply cancelled.");
        onClose?.();
    };

    // Auto-start recording once
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        startRecording();
        return () => sr.stop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-dismiss after sent
    useEffect(() => {
        if (stage !== STAGES.SENT) return undefined;
        const t = setTimeout(() => onClose?.(), 3500);
        return () => clearTimeout(t);
    }, [stage, onClose]);

    // ---- render ----------------------------------------------------------
    return (
        <section
            data-testid="voice-reply"
            data-stage={stage}
            aria-label={`Voice reply to ${recipient}`}
            className="rounded-3xl bg-gradient-to-b from-[#0A3D7A] to-[#082F5E] text-white p-6 sm:p-7 shadow-xl fade-in-up relative overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "14px 14px",
                }}
            />
            <div className="relative">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs tracking-[0.22em] font-semibold text-[#E0C875] uppercase">
                            Voice reply
                        </p>
                        <h3 className="font-display text-2xl font-bold mt-0.5 leading-tight">
                            {stage === STAGES.RECORDING && `Speak your reply`}
                            {stage === STAGES.DRAFT && `Review your reply`}
                            {stage === STAGES.CONFIRMING && `Confirm send`}
                            {stage === STAGES.SENT && `Sent`}
                        </h3>
                        <p className="text-white/80 text-sm mt-0.5">
                            to {recipient}
                        </p>
                    </div>
                    {stage !== STAGES.SENT && (
                        <button
                            type="button"
                            onClick={doCancelAll}
                            aria-label="Cancel voice reply"
                            data-testid="voice-reply-cancel-btn"
                            className="shrink-0 w-10 h-10 rounded-xl text-white/80 hover:bg-white/10 flex items-center justify-center"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {stage === STAGES.RECORDING && (
                    <div className="mt-5">
                        <div className="flex items-center justify-center relative h-32">
                            <span
                                aria-hidden="true"
                                className="absolute w-24 h-24 rounded-full bg-[#B8860B]/40 pulse-ring"
                            />
                            <span
                                aria-hidden="true"
                                className="absolute w-24 h-24 rounded-full bg-[#B8860B]/30 pulse-ring"
                                style={{ animationDelay: "0.6s" }}
                            />
                            <div className="relative w-20 h-20 rounded-full bg-[#B8860B] flex items-center justify-center shadow-2xl">
                                <Mic
                                    className="w-9 h-9 text-[#1A1A2E]"
                                    aria-hidden="true"
                                    strokeWidth={2.5}
                                />
                            </div>
                        </div>
                        <div
                            className="flex items-end justify-center gap-1.5 h-12 mt-1"
                            aria-hidden="true"
                        >
                            {BAR_DELAYS.map((d, i) => (
                                <span
                                    key={`reply-bar-${i}-${d}`}
                                    className="voice-bar w-2 h-full rounded-full bg-white/80"
                                    style={{ animationDelay: `${d}ms` }}
                                />
                            ))}
                        </div>
                        <div
                            className="mt-4 min-h-[64px] rounded-2xl bg-white/10 border border-white/15 p-3 backdrop-blur-sm"
                            data-testid="voice-reply-live-transcript"
                            aria-live="polite"
                        >
                            {sr.transcript ? (
                                <p className="text-white text-base leading-snug">
                                    {sr.transcript}
                                </p>
                            ) : (
                                <p className="text-white/60 text-sm italic">
                                    {sr.supported
                                        ? "Listening… speak naturally."
                                        : "Voice input isn't available here. Tap Stop to type your reply."}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={stopRecording}
                            data-testid="voice-reply-stop-btn"
                            className="mt-5 w-full min-h-[56px] rounded-2xl bg-white text-[#0A3D7A] text-lg font-bold flex items-center justify-center gap-2 hover:bg-[#FFF8F0] transition-colors shadow-lg"
                        >
                            <Square className="w-5 h-5" aria-hidden="true" />
                            Stop &amp; review
                        </button>
                    </div>
                )}

                {stage === STAGES.DRAFT && (
                    <div className="mt-5">
                        <label
                            htmlFor="voice-reply-textarea"
                            className="block text-xs uppercase tracking-wider font-semibold text-[#E0C875] mb-2"
                        >
                            Your reply
                        </label>
                        <textarea
                            id="voice-reply-textarea"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onFocus={() => {
                                if (draftFocusedRef.current) return;
                                draftFocusedRef.current = true;
                                speak(
                                    "Reply text area. Type your message, or tap Record again to dictate.",
                                );
                            }}
                            onBlur={() => {
                                draftFocusedRef.current = false;
                            }}
                            placeholder="Tap Record to dictate, or type your reply here."
                            rows={4}
                            data-testid="voice-reply-textarea"
                            className="w-full rounded-2xl bg-white text-[#1A1A2E] border-2 border-transparent focus:border-[#B8860B] focus:outline-none p-4 text-base leading-snug resize-y"
                        />
                        <div className="mt-4 space-y-2">
                            <button
                                type="button"
                                onClick={requestSend}
                                disabled={!draft.trim()}
                                data-testid="voice-reply-send-btn"
                                aria-label="Send reply"
                                className="w-full min-h-[56px] rounded-2xl bg-[#0A5C36] hover:bg-[#0a4d2d] disabled:opacity-40 disabled:cursor-not-allowed text-white text-lg font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                            >
                                <Send className="w-5 h-5" aria-hidden="true" />
                                Send
                            </button>
                            <button
                                type="button"
                                onClick={startRecording}
                                data-testid="voice-reply-rerecord-btn"
                                className="w-full min-h-[48px] rounded-2xl border-2 border-white/40 text-white/95 font-semibold hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                            >
                                <RefreshCcw className="w-4 h-4" aria-hidden="true" />
                                Record again
                            </button>
                        </div>
                    </div>
                )}

                {stage === STAGES.CONFIRMING && (
                    <div className="mt-5">
                        <div className="rounded-2xl bg-white/10 border-2 border-[#B8860B] backdrop-blur-sm p-4 mb-4">
                            <p className="text-xs uppercase tracking-wider font-semibold text-[#E0C875] mb-1">
                                Reply preview
                            </p>
                            <p
                                className="text-white text-base leading-snug whitespace-pre-wrap"
                                data-testid="voice-reply-preview"
                            >
                                {draft}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 mb-4 text-sm text-white/85">
                            <ShieldCheck
                                className="w-5 h-5 text-[#E0C875] shrink-0"
                                aria-hidden="true"
                            />
                            <p className="leading-snug">
                                Say{" "}
                                <span className="font-bold text-white">
                                    “send it”
                                </span>{" "}
                                to deliver, or tap below.
                            </p>
                        </div>
                        {sr.transcript && (
                            <p
                                className="text-xs text-white/70 italic mb-3"
                                data-testid="voice-reply-confirm-transcript"
                            >
                                Heard: “{sr.transcript}”
                            </p>
                        )}
                        <div className="space-y-2">
                            <button
                                type="button"
                                onClick={doSend}
                                data-testid="voice-reply-confirm-send-btn"
                                aria-label="Confirm and send reply now"
                                className="w-full min-h-[56px] rounded-2xl bg-[#0A5C36] hover:bg-[#0a4d2d] text-white text-lg font-bold flex items-center justify-center gap-2 transition-colors shadow-lg"
                            >
                                <Send className="w-5 h-5" aria-hidden="true" />
                                Confirm send
                            </button>
                            <button
                                type="button"
                                onClick={doCancelConfirm}
                                data-testid="voice-reply-edit-again-btn"
                                className="w-full min-h-[48px] rounded-2xl border-2 border-white/40 text-white/95 font-semibold hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                            >
                                <Pencil className="w-4 h-4" aria-hidden="true" />
                                Edit again
                            </button>
                        </div>
                    </div>
                )}

                {stage === STAGES.SENT && (
                    <div className="mt-6">
                        <div className="flex items-center gap-3">
                            <span
                                aria-hidden="true"
                                className="shrink-0 w-14 h-14 rounded-2xl bg-[#0A5C36] text-white flex items-center justify-center"
                            >
                                <CheckCircle2 className="w-7 h-7" />
                            </span>
                            <div>
                                <p className="font-display font-bold text-xl">
                                    Sent to {recipient}
                                </p>
                                <p className="text-white/85 text-sm">
                                    A copy is saved to your messages.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default VoiceReply;
